// =====================================================================================
// INVRT Radar — web console — the drawer's "Find people at this company" section
//
// Outside the United States a visit resolves at company level, so the question that
// follows "northwind.co.uk looked at pricing" is "who there do I talk to?". This panel
// answers it through the bridge (useCompanyBridge): a free preview with the exact price,
// then the reveal only after a person approves that figure on the one QuoteApproveCard
// every metered decision in this console goes through.
// =====================================================================================

import { Search as SearchIcon } from "lucide-react";
import { useBalance } from "@/hooks/useApi";
import { QuoteApproveCard } from "@/components/QuoteApproveCard";
import { RevealedContacts } from "@/components/RevealedContacts";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { pluralise } from "@/lib/format";
import type { QuoteResponse } from "@/lib/types";
import { useCompanyBridge, type BridgeQuote } from "./useCompanyBridge";

/**
 * The bridge echoes the quote row without two fields the approve card's prop type names
 * but never reads: `action` is `reveal_contact` by construction (the bridge delegates to
 * /leads/quote-reveal and nothing else), and the payload hash is held and re-verified on
 * the server at approval, so the console has no use for it. Neither is displayed.
 */
function toCardQuote(quote: BridgeQuote): QuoteResponse {
  return {
    quote_id: quote.quote_id,
    action: "reveal_contact",
    unit_count: quote.unit_count,
    credits: quote.credits,
    payload_hash: "",
    expires_at: quote.expires_at,
  };
}

export function FindPeoplePanel({
  domain,
  headingLevel = 3,
}: {
  domain: string;
  /** h2 when the host page's title is an h1 (the dossier page); h3 under a dialog's h2. */
  headingLevel?: 2 | 3;
}) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const bridge = useCompanyBridge();
  const balance = useBalance();

  const preview = bridge.preview;
  const quote = preview?.quote ?? null;

  return (
    <section aria-labelledby="contact-drawer-find-people" className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Heading id="contact-drawer-find-people" className="text-sm font-semibold text-ink">
          People at this company
        </Heading>
        <p className="text-xs text-ink-muted">
          Preview the decision-makers at {domain} and the exact price to reveal them.
          Previewing is free; credits move only after you approve the figure.
        </p>
      </div>

      {bridge.phase === "idle" || bridge.phase === "failed" ? (
        <div className="flex flex-col gap-3">
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void bridge.previewFor(domain)}
            >
              <SearchIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Find people at this company
            </Button>
          </div>
          {bridge.phase === "failed" ? <ErrorState error={bridge.error} /> : null}
        </div>
      ) : null}

      {bridge.phase === "previewing" ? (
        <LoadingState label={`Finding people at ${domain}…`} />
      ) : null}

      {bridge.phase === "nothing_to_reveal" && preview ? (
        <div className="flex flex-col gap-3">
          <EmptyState
            title={
              preview.token === "all_candidates_suppressed"
                ? `Everyone found at ${domain} is on your suppression list`
                : `No people found at ${domain}`
            }
            body={
              preview.token === "all_candidates_suppressed"
                ? "Radar will not price a contact you already own. Nothing was charged."
                : "The prospecting service has nobody on record for this domain right now. Nothing was charged."
            }
          />
          <div>
            <Button variant="ghost" size="sm" onClick={bridge.reset}>
              Start again
            </Button>
          </div>
        </div>
      ) : null}

      {(bridge.phase === "awaiting_approval" || bridge.phase === "revealing") && preview && quote ? (
        <div className="flex flex-col gap-3">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Title</TableHeaderCell>
                <TableHeaderCell>Company</TableHeaderCell>
                <TableHeaderCell>Location</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.candidates.map((candidate) => (
                <TableRow key={candidate.provider_ref}>
                  <TableCell className="font-medium">{candidate.title ?? "—"}</TableCell>
                  <TableCell className="text-ink-muted">{candidate.company_name ?? "—"}</TableCell>
                  <TableCell className="text-ink-muted">{candidate.location ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-xs text-ink-muted">
            {pluralise(preview.candidates.length, "person", "people")} can be revealed.
            {preview.suppressed_count > 0
              ? ` ${pluralise(preview.suppressed_count, "person", "people")} on your suppression list ${preview.suppressed_count === 1 ? "was" : "were"} left out and will not be charged for.`
              : ""}
          </p>
          <QuoteApproveCard
            quote={toCardQuote(quote)}
            balance={balance.data?.balance ?? null}
            approving={bridge.phase === "revealing"}
            onApprove={() => void bridge.approve()}
            onCancel={bridge.cancel}
            error={bridge.error}
          />
        </div>
      ) : null}

      {bridge.phase === "done" && bridge.result ? (
        <div className="flex flex-col gap-3">
          <RevealedContacts result={bridge.result} />
          <p className="text-xs text-ink-muted">
            These people now appear under Revealed people in your contacts.
          </p>
          <div>
            <Button variant="ghost" size="sm" onClick={bridge.reset}>
              Start again
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
